<script>
	/* eslint-disable no-unused-vars */
	// Components
	import TextInput from '@/Components/NewTextInput.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	// Partials
	import UserCriteriaSelector from '../UserCriteriaSelector.svelte';
	// Constants
	import {
		UserStatus,
		UserStatusName,
		UserRating,
		UserRatingName,
		UserSubRating,
		UserSubRatingName,
		UserStage,
		UserStageName
	} from '@/Constants/User.js';
	import { States } from '@/Constants/Address.js';
	// Services

	export let user_criteria;
	export let account_managers = [];
	export let is_update = false;

	let default_user_criteria = {
		account_status: {
			operator: '=',
			value: null
		},
		client_state: {
			operator: '=',
			value: null
		},
		client_category: {
			operator: '=',
			value: null
		},
		rating: {
			operator: '=',
			value: null
		},
		sub_rating: {
			operator: '=',
			value: null,
			value_between: null
		},
		life_stage: {
			operator: '=',
			value: null,
			value_between: null
		},
		custom_group: {
			operator: '=',
			value: null
		},
		lifetime_net_win: {
			operator: '>=',
			value: null
		},
		last_bet_made_in: {
			operator: null,
			value: null,
			value_between: null
		},
		bet_count: {
			operator: null,
			value: null,
			value_between: null
		},
		balance: {
			operator: null,
			value: null,
			value_between: null
		},
		last_transaction: {
			operator: '=',
			value: null
		},
		net_win_margin_lifetime: {
			operator: null,
			value: null,
			value_between: null
		},
		net_win_margin_90_days: {
			operator: null,
			value: null,
			value_between: null
		},
		greatest_amount_deposited: {
			operator: null,
			value: null,
			value_between: null
		},
		remaining_deposit_limit: {
			operator: null,
			value: null,
			value_between: null
		},
		average_bet_size: {
			operator: null,
			value: null,
			value_between: null
		},
		last_deposit_made: {
			operator: null,
			value: null,
			value_between: null
		},
		last_withdrawal_made: {
			operator: null,
			value: null,
			value_between: null
		},
		pending_bets: {
			operator: null,
			value: null,
			value_between: null
		},
		bonus_cost_percentage: {
			operator: null,
			value: null,
			value_between: null
		},
		bonus_cost_gross_win_ratio_lifetime: {
			operator: null,
			value: null,
			value_between: null
		},
		bonus_cost_gross_win_ratio_90_days: {
			operator: null,
			value: null,
			value_between: null
		},
		net_deposits_7_days: {
			operator: null,
			value: null,
			value_between: null
		},
		net_deposits_14_days: {
			operator: null,
			value: null,
			value_between: null
		},
		net_deposits_30_days: {
			operator: null,
			value: null,
			value_between: null
		},
		net_deposits_90_days: {
			operator: null,
			value: null,
			value_between: null
		},
		net_deposits_lifetime: {
			operator: null,
			value: null,
			value_between: null
		},
		total_withdrawals_7_days: {
			operator: null,
			value: null,
			value_between: null
		},
		registration_date: {
			operator: null,
			value: null,
			value_between: null
		},
		has_deposit_limit: {
			operator: '=',
			value: null
		}
	};

	user_criteria = Object.assign({}, default_user_criteria, user_criteria);

	export let errors = {};

	const ClientCategories = () => {
		const categories = {};
		for (const manager of Object.keys(account_managers)) {
			// eslint-disable-next-line quotes
			categories[manager] = 'Managed by ' + account_managers[manager];
		}
		categories['unmanaged'] = 'Unmanaged';
		return categories;
	};

	const AccountStatuses = () => {
		const statuses = {};
		for (const status of Object.keys(UserStatus)) {
			if ([UserStatus.Active, UserStatus.Locked].includes(UserStatus[status])) {
				// eslint-disable-next-line quotes
				statuses[UserStatusName[UserStatus[status]]] = UserStatus[status];
			}
		}

		return statuses;
	};

	const UserRatings = () => {
		const ratings = {};
		for (const rating of Object.keys(UserRating)) {
			if (
				![
					UserRating.BONUS_ABUSER,
					UserRating.FEMALE,
					UserRating.TIPPING,
					UserRating.TOUGH_SPORT,
					UserRating.TOUGH
				].includes(UserRating[rating])
			) {
				// eslint-disable-next-line quotes
				ratings[UserRatingName[UserRating[rating]]] = UserRating[rating];
			}
		}

		return ratings;
	};

	const UserStages = () => {
		const stages = {};
		for (const rating of Object.keys(UserStage)) {
			// eslint-disable-next-line quotes
			stages[UserStageName[UserStage[rating]]] = UserStage[rating];
		}
		stages['No Stage'] = null;
		return stages;
	};

	function updateFilter(e, singleSelection) {
		if (singleSelection) {
			user_criteria[e.detail.name].value = e.detail.checked[0];
		} else {
			user_criteria[e.detail.name].value = e.detail.checked;
		}
	}
</script>

<div class="flex flex-row w-full py-1 text-left border-t border-b justify-between mt-2">
	<div class="flex flex-row items-center justify-center w-1/6 border-r border-l">
		<PillMultiSelect
			name="account_status"
			label="Account Status:"
			className="w-40"
			altLabel={true}
			disabled={is_update}
			singleSelection={false}
			on:change={(e) => updateFilter(e)}
			checked={user_criteria.account_status.value ?? []}
			options={Object.entries(AccountStatuses()).map(([key, value]) => [value, key])}
			action=""
		/>
	</div>
	<div class="flex flex-row items-center justify-center w-1/6 border-r">
		<PillMultiSelect
			name="client_state"
			label="Territory State:"
			className="w-40"
			altLabel={true}
			disabled={is_update}
			singleSelection={false}
			on:change={(e) => updateFilter(e)}
			checked={user_criteria.client_state.value ?? []}
			options={Object.entries(States)}
			action=""
		/>
	</div>
	<div class="flex flex-row items-center justify-center w-1/6 border-r">
		<PillMultiSelect
			name="client_category"
			label="Client Manager:"
			className="w-40"
			altLabel={true}
			disabled={is_update}
			singleSelection={false}
			on:change={(e) => updateFilter(e)}
			checked={user_criteria.client_category.value ?? []}
			options={Object.entries(ClientCategories())}
			action=""
		/>
	</div>
	<div class="flex flex-row items-center justify-center w-1/6 border-r">
		<PillMultiSelect
			name="rating"
			label="Client Rating:"
			className="w-40"
			altLabel={true}
			disabled={is_update}
			singleSelection={false}
			on:change={(e) => updateFilter(e)}
			checked={user_criteria.rating.value ?? []}
			options={Object.entries(UserRatings()).map(([key, value]) => [value, key])}
			action=""
		/>
	</div>
	<div class="flex flex-row items-center justify-center w-1/6 border-r">
		<PillMultiSelect
			name="sub_rating"
			label="Client Sub-Rating:"
			className="w-40"
			altLabel={true}
			disabled={is_update}
			singleSelection={false}
			on:change={(e) => updateFilter(e)}
			checked={user_criteria.sub_rating.value ?? []}
			options={Object.entries(UserSubRating).map(([key, value]) => [value, UserSubRatingName[value]])}
			action=""
		/>
	</div>
	<div class="flex flex-row items-center justify-center w-1/6 border-r">
		<PillMultiSelect
			name="life_stage"
			label="Client Life Stage:"
			className="w-40"
			altLabel={true}
			disabled={is_update}
			singleSelection={false}
			on:change={(e) => updateFilter(e)}
			checked={user_criteria.life_stage.value ?? []}
			options={Object.entries(UserStages()).map(([key, value]) => [value, key])}
			action=""
		/>
	</div>
</div>
<div class="flex flex-row w-full py-1 text-left space-x-1 border-b">
	<div class="flex flex-row space-x-2 w-1/4 last:border-r-0 border-r first:border-l first:pl-1">
		<TextInput
			className="w-64"
			label="Lifetime net win:"
			labelClass="mb-1"
			name="lifetime_net_win"
			type="number"
			placeholder="Enter amount"
			step="1"
			prefix="$"
			disabled={is_update}
			errors={errors && errors['user_criteria.lifetime_net_win.value']}
			bind:value={user_criteria.lifetime_net_win.value}
		/>
	</div>
	<UserCriteriaSelector
		label="Last bet made"
		value_label="Days"
		{errors}
		name="last_bet_made_in"
		between={true}
		type="number"
		placeholder="Days"
		min="0"
		step="1"
		disabled={is_update}
		bind:operator={user_criteria.last_bet_made_in.operator}
		bind:value={user_criteria.last_bet_made_in.value}
		bind:value_between={user_criteria.last_bet_made_in.value_between}
	/>
	<UserCriteriaSelector
		label="Bet count"
		value_label="Bets"
		{errors}
		name="bet_count"
		type="number"
		placeholder="Bets"
		min="0"
		step="1"
		disabled={is_update}
		bind:operator={user_criteria.bet_count.operator}
		bind:value={user_criteria.bet_count.value}
		bind:value_between={user_criteria.bet_count.value_between}
	/>
	<UserCriteriaSelector
		label="Balance"
		value_label="Amount"
		{errors}
		name="balance"
		type="number"
		min="0"
		step="1"
		prefix="$"
		disabled={is_update}
		bind:operator={user_criteria.balance.operator}
		bind:value={user_criteria.balance.value}
		bind:value_between={user_criteria.balance.value_between}
	/>
</div>
<div class="flex flex-row w-full py-1 text-left space-x-1 border-b">
	<UserCriteriaSelector
		label="Net win margin 90 days"
		value_label="Percent"
		{errors}
		name="net_win_margin_90_days"
		type="number"
		step="0.01"
		suffix="%"
		disabled={is_update}
		bind:operator={user_criteria.net_win_margin_90_days.operator}
		bind:value={user_criteria.net_win_margin_90_days.value}
		bind:value_between={user_criteria.net_win_margin_90_days.value_between}
	/>

	<UserCriteriaSelector
		label="Net win margin lifetime"
		value_label="Percent"
		{errors}
		name="net_win_margin_lifetime"
		type="number"
		step="0.01"
		suffix="%"
		disabled={is_update}
		bind:operator={user_criteria.net_win_margin_lifetime.operator}
		bind:value={user_criteria.net_win_margin_lifetime.value}
		bind:value_between={user_criteria.net_win_margin_lifetime.value_between}
	/>

	<UserCriteriaSelector
		label="Greatest amount deposited"
		value_label="Amount"
		{errors}
		name="greatest_amount_deposited"
		type="number"
		min="0"
		step="1"
		prefix="$"
		disabled={is_update}
		bind:operator={user_criteria.greatest_amount_deposited.operator}
		bind:value={user_criteria.greatest_amount_deposited.value}
		bind:value_between={user_criteria.greatest_amount_deposited.value_between}
	/>
	<UserCriteriaSelector
		label="Remaining deposit limit"
		value_label="Amount"
		{errors}
		name="remaining_deposit_limit"
		type="number"
		min="0"
		step="1"
		prefix="$"
		disabled={is_update}
		available_operators={{ '>=': '&gt;&equals;', between: 'Between' }}
		bind:operator={user_criteria.remaining_deposit_limit.operator}
		bind:value={user_criteria.remaining_deposit_limit.value}
		bind:value_between={user_criteria.remaining_deposit_limit.value_between}
	/>
</div>
<div class="flex flex-row w-full py-1 text-left space-x-1 border-b">
	<div
		class="flex flex-row space-x-2 w-1/4 last:border-r-0 border-r first:border-l first:pl-1 justify-center items-center"
	>
		<PillMultiSelect
			name="has_deposit_limit"
			label="Has a Deposit Limit:"
			className="w-60"
			altLabel={true}
			disabled={is_update}
			singleSelection={true}
			on:change={(e) => updateFilter(e, true)}
			checked={user_criteria.has_deposit_limit.value ? [user_criteria.has_deposit_limit.value] : []}
			options={Object.entries({
				'': 'N/A',
				true: 'Yes',
				false: 'No'
			})}
			action=""
		/>
	</div>
	<div
		class="flex flex-row space-x-2 w-1/4 last:border-r-0 border-r first:border-l first:pl-1 justify-center items-center"
	>
		<PillMultiSelect
			name="last_transaction"
			label="Last transaction:"
			className="w-60"
			altLabel={true}
			disabled={is_update}
			singleSelection={true}
			on:change={(e) => updateFilter(e, true)}
			checked={user_criteria.last_transaction.value ? [user_criteria.last_transaction.value] : []}
			options={Object.entries({
				'': 'N/A',
				deposit: 'Deposit',
				withdrawal: 'Withdrawal',
				deposit_or_withdrawal: 'Deposit or Withdrawal'
			})}
			action=""
		/>
	</div>

	<UserCriteriaSelector
		label="Last withdrawal made"
		value_label="Days"
		{errors}
		name="last_withdrawal_made"
		type="number"
		min="0"
		step="1"
		placeholder="Days"
		disabled={is_update}
		bind:operator={user_criteria.last_withdrawal_made.operator}
		bind:value={user_criteria.last_withdrawal_made.value}
		bind:value_between={user_criteria.last_withdrawal_made.value_between}
	/>

	<UserCriteriaSelector
		label="Last deposit made"
		value_label="Days"
		{errors}
		name="last_deposit_made"
		type="number"
		min="0"
		step="1"
		placeholder="Days"
		disabled={is_update}
		bind:operator={user_criteria.last_deposit_made.operator}
		bind:value={user_criteria.last_deposit_made.value}
		bind:value_between={user_criteria.last_deposit_made.value_between}
	/>
</div>
<div class="flex flex-row w-full py-1 text-left space-x-1 border-b">
	<UserCriteriaSelector
		label="Bonus cost percentage"
		value_label="Percent"
		{errors}
		name="bonus_cost_percentage"
		type="number"
		step="0.01"
		suffix="%"
		disabled={is_update}
		bind:operator={user_criteria.bonus_cost_percentage.operator}
		bind:value={user_criteria.bonus_cost_percentage.value}
		bind:value_between={user_criteria.bonus_cost_percentage.value_between}
	/>
	<UserCriteriaSelector
		label="Average bet size"
		value_label="Amount"
		{errors}
		name="average_bet_size"
		type="number"
		min="0"
		step="1"
		prefix="$"
		disabled={is_update}
		bind:operator={user_criteria.average_bet_size.operator}
		bind:value={user_criteria.average_bet_size.value}
		bind:value_between={user_criteria.average_bet_size.value_between}
	/>
	<UserCriteriaSelector
		label="Bonus Cost / Gross win ratio - Lifetime"
		value_label="Percent"
		{errors}
		name="bonus_cost_gross_win_ratio_lifetime"
		type="number"
		step="0.01"
		suffix="%"
		disabled={is_update}
		bind:operator={user_criteria.bonus_cost_gross_win_ratio_lifetime.operator}
		bind:value={user_criteria.bonus_cost_gross_win_ratio_lifetime.value}
		bind:value_between={user_criteria.bonus_cost_gross_win_ratio_lifetime.value_between}
	/>
	<UserCriteriaSelector
		label="Bonus Cost / Gross win ratio - 90 Days"
		value_label="Percent"
		{errors}
		name="bonus_cost_gross_win_ratio_90_days"
		type="number"
		step="0.01"
		suffix="%"
		disabled={is_update}
		bind:operator={user_criteria.bonus_cost_gross_win_ratio_90_days.operator}
		bind:value={user_criteria.bonus_cost_gross_win_ratio_90_days.value}
		bind:value_between={user_criteria.bonus_cost_gross_win_ratio_90_days.value_between}
	/>
</div>
<div class="flex flex-row w-full py-1 text-left space-x-1 border-b">
	<UserCriteriaSelector
		label="Net Deposits - Last 7 Days"
		value_label="Amount"
		{errors}
		name="net_deposits_7_days"
		type="number"
		min="0"
		step="1"
		prefix="$"
		disabled={is_update}
		bind:operator={user_criteria.net_deposits_7_days.operator}
		bind:value={user_criteria.net_deposits_7_days.value}
		bind:value_between={user_criteria.net_deposits_7_days.value_between}
	/>
	<UserCriteriaSelector
		label="Net Deposits - Last 14 Days"
		value_label="Amount"
		{errors}
		name="net_deposits_14_days"
		type="number"
		min="0"
		step="1"
		prefix="$"
		disabled={is_update}
		bind:operator={user_criteria.net_deposits_14_days.operator}
		bind:value={user_criteria.net_deposits_14_days.value}
		bind:value_between={user_criteria.net_deposits_14_days.value_between}
	/>
	<UserCriteriaSelector
		label="Net Deposits - Last 30 Days"
		value_label="Amount"
		{errors}
		name="net_deposits_30_days"
		type="number"
		min="0"
		step="1"
		prefix="$"
		disabled={is_update}
		bind:operator={user_criteria.net_deposits_30_days.operator}
		bind:value={user_criteria.net_deposits_30_days.value}
		bind:value_between={user_criteria.net_deposits_30_days.value_between}
	/>
	<UserCriteriaSelector
		label="Net Deposits - Last 90 Days"
		value_label="Amount"
		{errors}
		name="net_deposits_90_days"
		type="number"
		min="0"
		step="1"
		prefix="$"
		disabled={is_update}
		bind:operator={user_criteria.net_deposits_90_days.operator}
		bind:value={user_criteria.net_deposits_90_days.value}
		bind:value_between={user_criteria.net_deposits_90_days.value_between}
	/>
</div>
<div class="flex flex-row w-full py-1 text-left space-x-1">
	<UserCriteriaSelector
		label="Net Deposits - Lifetime"
		value_label="Amount"
		{errors}
		name="net_deposits_lifetime"
		type="number"
		min="0"
		step="1"
		prefix="$"
		disabled={is_update}
		bind:operator={user_criteria.net_deposits_lifetime.operator}
		bind:value={user_criteria.net_deposits_lifetime.value}
		bind:value_between={user_criteria.net_deposits_lifetime.value_between}
	/>
	<UserCriteriaSelector
		label="Total Withdrawals - 7 Days"
		value_label="Amount"
		{errors}
		name="total_withdrawals_7_days"
		type="number"
		min="0"
		step="1"
		prefix="$"
		disabled={is_update}
		bind:operator={user_criteria.total_withdrawals_7_days.operator}
		bind:value={user_criteria.total_withdrawals_7_days.value}
		bind:value_between={user_criteria.total_withdrawals_7_days.value_between}
	/>
	<UserCriteriaSelector
		label="Pending bets"
		value_label="Bets"
		{errors}
		name="pending_bets"
		type="number"
		min="0"
		step="1"
		placeholder="Bets"
		disabled={is_update}
		bind:operator={user_criteria.pending_bets.operator}
		bind:value={user_criteria.pending_bets.value}
		bind:value_between={user_criteria.pending_bets.value_between}
	/>
	<UserCriteriaSelector
		label="Registration date"
		value_label="Days"
		{errors}
		name="registration_date"
		type="number"
		placeholder="Days"
		min="0"
		step="1"
		disabled={is_update}
		bind:operator={user_criteria.registration_date.operator}
		bind:value={user_criteria.registration_date.value}
		bind:value_between={user_criteria.registration_date.value_between}
	/>
</div>
